/**
 * ARC-59 — risk assessment write guard (pure; no Firestore, no React).
 *
 * THE INCIDENT THIS EXISTS FOR
 * On 2026-08-18 a completed assessment in `financial_data/{uid}.riskAssessmentData`
 * — 15 answers, results set — was replaced by a fresh empty one
 * (`answers: {}`, `results: null`), and `userAge` went back to its default 30.
 *
 * That pair of symptoms names the mechanism. `userAge` is only assigned when the
 * load sees a value (`if (d.userAge) setUserAge(...)`), so a document written
 * with `userAge: 30` was written by a session whose load never delivered one.
 * Two load branches leave the dashboard in exactly that state — the failure
 * branch, and the branch for a read that succeeds but carries no payload — and
 * neither of them assigns `riskAssessmentData` either. It stays `null`.
 *
 * `null` is where the loss starts. RiskAssessment seeds its local state from the
 * prop ONCE, in useState initialisers, then an effect mirrors that local state
 * straight back to the parent on mount. Mount with the prop still `null` and the
 * component immediately hands the dashboard `{ answers: {}, results: null, ... }`
 * — the incident payload, assembled client-side, sitting in the same state
 * autosave serialises two seconds later. The member never touched the tab's
 * controls; opening it was enough.
 *
 * The empty-payload branch sets `loadedOk.current = true`, so nothing stopped
 * that write. ARC-14's pre-write reconcile did not either: it protects expenses,
 * and `riskAssessmentData` is written raw.
 *
 * The rules here follow from that:
 *
 *   1. A read we do not trust produces no assessment state at all and no write
 *      permission — `resolveRiskLoad`. Same closed state for a read that failed
 *      and a read whose shape we cannot parse, because both leave us equally
 *      ignorant of what the server holds.
 *   2. An empty assessment never lands on top of a completed one unless the
 *      member asked for that — `guardRiskWrite`. Checked against the server's
 *      current copy as well as our own snapshot, since a stale snapshot is
 *      exactly the situation where the check matters most.
 *
 * Pure, so both failure paths are testable without a Firestore.
 *
 * Non-goals (ARC-59): nothing here touches questions, scoring, or the
 * Strategy Automation risk-alignment consumer.
 */

export const RISK_LOAD_ERROR =
  "Could not load your risk profile. The assessment is unavailable so nothing overwrites your saved results — please refresh the page.";

export const RISK_OVERWRITE_BLOCKED =
  "Your saved risk profile was protected: an empty assessment was about to replace it. Nothing was overwritten. Use “Retake Assessment” if you meant to start over.";

/* A plain object, which is the only shape an assessment is ever stored in.
   Arrays are excluded deliberately: `[].answers` is undefined, so an array
   would read as "empty assessment" and be treated as a safe thing to replace. */
const isPlainObject = (v) =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/* Absent means "the member has not taken the assessment yet" — a normal, safe
   state. Anything else that is not a plain object is a value we do not
   understand, which is NOT the same thing and must not be treated as one. */
const isAbsent = (v) => v === undefined || v === null;

export const isUnparseableAssessment = (v) => !isAbsent(v) && !isPlainObject(v);

/**
 * Does this assessment hold member work worth protecting?
 *
 * Either half counts on its own. `results` without `answers` is what a report
 * restored from an older schema looks like, and `answers` without `results` is a
 * part-finished run — losing either is the data loss this module prevents.
 */
export const isCompletedAssessment = (v) => {
  if (!isPlainObject(v)) return false;
  const answered =
    isPlainObject(v.answers) && Object.keys(v.answers).length > 0;
  return answered || !isAbsent(v.results);
};

/**
 * Is this the blank assessment a fresh mount produces?
 *
 * Note this is not `!isCompletedAssessment` — an unparseable value is neither
 * empty nor completed, and must never be treated as safe to overwrite.
 * `currentQuestionIndex` / `showResults` / `showReport` are ignored on purpose:
 * scrolling to question 4 without answering it is still nothing worth keeping.
 */
export const isEmptyAssessment = (v) => {
  if (isAbsent(v)) return true;
  if (!isPlainObject(v)) return false;
  return !isCompletedAssessment(v);
};

/**
 * Decide the risk assessment state a load result is allowed to produce.
 *
 * @param {{ok: boolean, data?: object|null}} result
 *   `ok` is false when the read threw or the service reported failure.
 *   `data` is the loaded document, or null/empty for a brand-new account.
 *
 * @returns {{riskAssessmentData: object|null,
 *   committed: {riskAssessmentData: object|null}|null,
 *   canSave: boolean, error: string}}
 *
 * `committed` carries the distinction the old code could not express, because
 * it used one `null` for both meanings:
 *   - `null`                            → we do not know what the server holds.
 *   - `{ riskAssessmentData: null }`     → the server confirmed there is none.
 * Only the second is safe to write over.
 *
 * `canSave: false` means the assessment must not be rendered at all — a blank
 * one on screen is a blank one in the save path.
 */
export const resolveRiskLoad = ({ ok, data } = {}) => {
  const failedRead = () => ({
    riskAssessmentData: null,
    committed: null,
    canSave: false,
    error: RISK_LOAD_ERROR,
  });

  if (!ok) return failedRead();

  /* A present-but-unreadable value is the quiet version of the same bug: the
     component's optional chaining turns it into `{ answers: {}, results: null }`
     on screen, and autosave then writes that over a server value we simply
     failed to parse. Ignoring a value we cannot read is fine; replacing it is
     not. */
  if (isUnparseableAssessment(data?.riskAssessmentData)) return failedRead();

  const riskAssessmentData = isAbsent(data?.riskAssessmentData)
    ? null
    : data.riskAssessmentData;

  return {
    riskAssessmentData,
    committed: { riskAssessmentData },
    canSave: true,
    error: "",
  };
};

/**
 * Last gate before `riskAssessmentData` goes into a write.
 *
 * @param {{outgoing: object|null,
 *   committed: {riskAssessmentData: object|null}|null,
 *   server: object|null,
 *   retakeAcknowledged: boolean,
 *   outgoingTrusted: boolean}} params
 *   `server` is the document's current `riskAssessmentData`, read immediately
 *   before the write. `committed` is our own last server-confirmed snapshot.
 *   `outgoingTrusted` is false when the load that should have produced
 *   `outgoing` failed or could not be parsed.
 *
 * @returns {{riskAssessmentData: object|null, blocked: boolean, warning: string}}
 *   `riskAssessmentData` is what may actually be written. `blocked` is for the
 *   member-facing case only — an empty assessment stopped from replacing a
 *   completed one — so the dashboard knows when to warn.
 *
 * The value is substituted rather than the whole save being refused. One save
 * carries every tab's state in a single document write, so refusing it would
 * strand a member editing their expenses because of a tab they never opened.
 * Substituting keeps the write moving and still guarantees the assessment on
 * the server is never the one replaced.
 */
export const guardRiskWrite = ({
  outgoing,
  committed,
  server,
  retakeAcknowledged = false,
  outgoingTrusted = true,
} = {}) => {
  /* `undefined` is never emitted: Firestore treats it as a field to reject or
     strip rather than as a value, so "no assessment" is always spelled null. */
  const allow = (value) => ({
    riskAssessmentData: value === undefined ? null : value,
    blocked: false,
    warning: "",
  });

  /* We could not read this field, so `outgoing` is not the member's assessment
     — it is whatever the UI defaulted to in its absence. Write the server's own
     value straight back, which makes the field a no-op for this save.

     Deliberately verbatim, including a value we failed to parse: we do not know
     it is safe to lose. Data preserved beats data replaced by a blank, and the
     tab is already showing a load error rather than the assessment. */
  if (!outgoingTrusted) return allow(server);

  /* A retake IS an empty assessment replacing a completed one. That is the
     whole point of the button, so an acknowledged one passes untouched. */
  if (retakeAcknowledged) return allow(outgoing);

  if (!isEmptyAssessment(outgoing)) return allow(outgoing);

  /* Both copies are consulted. `server` is authoritative and catches the case
     our snapshot is stale — another session finished the assessment after we
     loaded. `committed` covers the write where the pre-write read gave us
     nothing to compare against. */
  const priorCandidates = [server, committed?.riskAssessmentData];
  const prior = priorCandidates.find((c) => isCompletedAssessment(c));

  if (!prior) return allow(outgoing);

  return {
    riskAssessmentData: prior,
    blocked: true,
    warning: RISK_OVERWRITE_BLOCKED,
  };
};

export default resolveRiskLoad;
